import { type JSX, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  type Capacidades,
  type ConversaAtiva,
  type EstadoBot,
  EXPLICACAO_ESTADO,
  ROTULO_ESTADO,
  api,
  explicarMotivo,
} from '../lib/api.js';

/**
 * Estado do bot numa conversa, com as ações.
 *
 * Usado na aba Conversas e no detalhe do atendimento — o mesmo componente nos
 * dois, porque é a mesma decisão: quem percebe que o bot está calado precisa
 * destravar ali, não ir procurar outra tela.
 */

function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** "há 12 min", "há 2 h". A duração importa mais que o horário exato. */
export function desde(iso: string): string {
  const minutos = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  return `há ${horas} h${minutos % 60 > 0 ? ` ${minutos % 60} min` : ''}`;
}

/**
 * Cor do selo de estado — com o TEXTO do estado sempre junto.
 *
 * A cor é reforço, não o sinal: no tema escuro ela muda de peso, e para quem
 * não distingue cores ela não diz nada. O rótulo é que informa.
 */
const COR_ESTADO: Record<EstadoBot, string> = {
  bot: 'green',
  humano: 'orange',
  pausado_auto: 'orange',
};

export function FraseDoEstado({ conversa }: { conversa: ConversaAtiva }): JSX.Element {
  const partes = [`desde ${hora(conversa.estadoDesde)}`];
  if (conversa.expiraEm) partes.push(`expira ${hora(conversa.expiraEm)}`);

  return (
    <Group gap={6} wrap="nowrap">
      <Badge color={COR_ESTADO[conversa.estado]} variant="light">
        {ROTULO_ESTADO[conversa.estado]}
      </Badge>
      <Text size="xs" c="dimmed">
        ({partes.join(', ')})
      </Text>
    </Group>
  );
}

export function AcoesDoBot({
  conversa,
  capacidades,
  aoMudar,
}: {
  conversa: ConversaAtiva;
  capacidades: Capacidades;
  aoMudar: () => void;
}): JSX.Element | null {
  const [erro, setErro] = useState<string | null>(null);
  const [agindo, setAgindo] = useState(false);
  /** Encerrar pede confirmação; as outras duas, não. */
  const [confirmando, { open: abrirConfirmacao, close: fecharConfirmacao }] =
    useDisclosure(false);

  if (!capacidades.editar) {
    return (
      <Text size="xs" c="dimmed">
        Seu papel não altera o estado da conversa.
      </Text>
    );
  }

  async function encerrar(): Promise<void> {
    setAgindo(true);
    try {
      const r = await api.post<{ haviaSessao: boolean; apagadas: number }>(
        '/admin/conversas/encerrar',
        { atendimento: conversa.atendimentoId },
      );
      setErro(null);
      fecharConfirmacao();
      // Distingue "encerrei" de "já não havia nada": a sessão pode ter
      // expirado entre a tela carregar e o clique.
      notifications.show({
        color: r.haviaSessao ? 'green' : 'gray',
        title: r.haviaSessao ? 'Sessão encerrada' : 'Nada a encerrar',
        message: r.haviaSessao
          ? 'A próxima mensagem do aluno começa do zero.'
          : 'Não havia sessão ativa — o aluno já começaria do zero.',
      });
      aoMudar();
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível encerrar a sessão'));
    } finally {
      setAgindo(false);
    }
  }

  async function agir(caminho: 'bot' | 'secretaria'): Promise<void> {
    setAgindo(true);
    try {
      await api.post(`/admin/conversas/${caminho}`, { atendimento: conversa.atendimentoId });
      setErro(null);
      notifications.show({
        color: 'green',
        message:
          caminho === 'bot'
            ? 'Conversa devolvida ao bot.'
            : 'Conversa passada para a secretaria.',
      });
      aoMudar();
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível mudar o estado'));
    } finally {
      setAgindo(false);
    }
  }

  return (
    <Stack gap="xs">
      {/* As duas que mudam QUEM atende ficam juntas. */}
      <Group gap="xs">
        {conversa.estado !== 'bot' && (
          <Button size="xs" loading={agindo} onClick={() => void agir('bot')}>
            Devolver ao bot
          </Button>
        )}
        {conversa.estado !== 'humano' && (
          <Button size="xs" loading={agindo} onClick={() => void agir('secretaria')}>
            Passar para a secretaria
          </Button>
        )}
      </Group>

      {/*
        Encerrar sessão fica À PARTE e discreto: as outras duas mudam quem
        atende e são reversíveis; esta DESCARTA o estado da conversa. Os três
        lado a lado convidariam ao clique errado.
      */}
      <Divider variant="dashed" />
      <Group>
        <Button
          variant="subtle"
          color="red"
          size="compact-xs"
          onClick={abrirConfirmacao}
        >
          Encerrar sessão
        </Button>
      </Group>

      <Modal
        opened={confirmando}
        onClose={fecharConfirmacao}
        title="Encerrar sessão desta conversa?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Isso descarta o estado desta conversa: onde a pessoa parou no menu, o que ela
            respondeu na qualificação e o contador de entradas inválidas. O histórico
            (atendimentos, protocolos, transbordos) <strong>fica</strong>, e a próxima mensagem
            dela começa do zero, com saudação.
          </Text>
          <Group justify="flex-end">
            <Button onClick={fecharConfirmacao}>Cancelar</Button>
            <Button color="red" variant="filled" loading={agindo} onClick={() => void encerrar()}>
              Encerrar mesmo assim
            </Button>
          </Group>
        </Stack>
      </Modal>

      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}
    </Stack>
  );
}

/**
 * Bloco de estado para o detalhe do atendimento.
 *
 * `conversa` nulo significa que NÃO há sessão ativa — o que não é erro: é o
 * estado padrão, e o bot responde normalmente. Dizer isso com palavras evita
 * que a ausência de informação pareça problema.
 */
export function EstadoNoDetalhe({
  conversa,
  capacidades,
  erroRedis,
  aoMudar,
}: {
  conversa: ConversaAtiva | null;
  capacidades: Capacidades;
  erroRedis: boolean;
  aoMudar: () => void;
}): JSX.Element {
  if (erroRedis) {
    return (
      <Alert color="yellow" variant="light" title="Não foi possível ler o estado desta conversa">
        O estado vive no Redis e ele não respondeu — não é o mesmo que “o bot está respondendo”.
        Tente recarregar.
      </Alert>
    );
  }

  if (!conversa) {
    return (
      <Text size="sm" c="dimmed">
        Sem sessão ativa: a conversa esfriou (30 min sem mensagem) e o bot responde normalmente na
        próxima.
      </Text>
    );
  }

  return (
    <Alert color={conversa.estado === 'bot' ? 'gray' : 'yellow'} variant="light">
      <Stack gap="xs">
        <FraseDoEstado conversa={conversa} />
        <Text size="xs" c="dimmed">
          {EXPLICACAO_ESTADO[conversa.estado]} · parou em <Code>{conversa.no}</Code>
        </Text>
        <AcoesDoBot conversa={conversa} capacidades={capacidades} aoMudar={aoMudar} />
      </Stack>
    </Alert>
  );
}
